import * as React from "react";
import { useMemo } from "react";
import trim from "lodash/trim";
import trunc from "lodash/truncate";
import * as JSON from "util/json";
import FieldType from "views/logic/fieldtypes/FieldType";
import { getPrettifiedValue } from "views/components/visualizations/utils/unitConverters";
import { UNIT_FEATURE_FLAG } from "views/components/visualizations/Constants";
import useFeature from "hooks/useFeature";
import { MISSING_BUCKET_NAME } from "views/Constants";
import formatValueWithUnitLabel from "views/components/visualizations/utils/formatValueWithUnitLabel";
import usePluginEntities from "hooks/usePluginEntities";
import EmptyValue from "./EmptyValue";
import DecoratorValue from "./DecoratorValue";
const usePluggableValueRenderer = () => {
  const pluggableValueRenderer = usePluginEntities("fieldTypeValueRenderer");
  return useMemo(
    () => Object.fromEntries((pluggableValueRenderer != null ? pluggableValueRenderer : []).map(({ type, render }) => [type, render])),
    [pluggableValueRenderer]
  );
};
const defaultComponent = ({ value }) => value;
const stringify = (value) => {
  switch (typeof value) {
    case "bigint":
      return value.toString();
    case "string":
      return value;
    default:
      return JSON.stringify(value);
  }
};
const _formatValue = (field, value, truncate, render, type) => {
  const stringified = stringify(value);
  const Component = render;
  return trim(stringified) === "" ? /* @__PURE__ */ React.createElement(EmptyValue, null) : /* @__PURE__ */ React.createElement(Component, { field, value: truncate ? trunc(stringified) : stringified, type });
};
const ValueWithUnitRenderer = ({ value, unit }) => {
  const prettified = getPrettifiedValue(value, { abbrev: unit.abbrev, unitType: unit.unitType });
  return /* @__PURE__ */ React.createElement("span", { title: value.toString() }, formatValueWithUnitLabel(prettified == null ? void 0 : prettified.value, prettified.unit.abbrev));
};
const FormattedValue = ({
  field,
  value = void 0,
  truncate = false,
  render = defaultComponent,
  unit = void 0,
  type = void 0
}) => {
  const unitFeatureEnabled = useFeature(UNIT_FEATURE_FLAG);
  const shouldRenderValueWithUnit = unitFeatureEnabled && (unit == null ? void 0 : unit.isDefined) && value && value !== MISSING_BUCKET_NAME && unitFeatureEnabled;
  if (shouldRenderValueWithUnit) return /* @__PURE__ */ React.createElement(ValueWithUnitRenderer, { value, unit });
  return _formatValue(field, value, truncate, render, type);
};
const TypeSpecificValue = ({
  field,
  value = void 0,
  render = defaultComponent,
  type = FieldType.Unknown,
  truncate = false,
  unit = void 0
}) => {
  const pluggableValueRenderer = usePluggableValueRenderer();
  if (value === void 0) {
    return null;
  }
  if (type.isDecorated()) {
    return /* @__PURE__ */ React.createElement(DecoratorValue, { value, field, render, type, truncate });
  }
  if (pluggableValueRenderer[type.type]) {
    return pluggableValueRenderer[type.type](value, field, render);
  }
  return /* @__PURE__ */ React.createElement(FormattedValue, { field, value, truncate, unit, render, type });
};
export default TypeSpecificValue;
